import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useSecurityPin } from '../context/SecurityPinContext';
import { AuditLogDialog } from '../components/common/AuditLogDialog';
import { PageHeader } from '../components/common/PageHeader';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Tabs, TabsList, TabsTrigger,
} from '../components/ui/tabs';
import {
  Percent, Plus, Search, Trash2, Tag, Power, Pencil, Euro, CalendarClock, Lock, ScrollText,
} from 'lucide-react';
import { AmountInput } from '../components/common/NumericKeypad';

const emptyForm = {
  code: '', type: 'percent', value: '', scope: 'all',
  category_ids: [], product_ids: [], max_uses: 0, expires_at: '', active: true,
};

const fmtDate = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return iso; }
};

export default function DiscountsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { runSensitive } = useSecurityPin();
  const canManage = user?.role === 'admin' || user?.role === 'manager';
  const [auditOpen, setAuditOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  // Switch on/off delle due funzioni sconto.
  const [dsettings, setDsettings] = useState({ codes_enabled: false, presets_enabled: false });

  const load = useCallback(async (search) => {
    const { data } = await api.get('/discounts', { params: search ? { q: search } : {} });
    setItems(data || []);
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cats, prods, st] = await Promise.all([
        api.get('/categories'),
        api.get('/products?light=true'),
        api.get('/discounts/settings'),
      ]);
      setCategories(cats.data || []);
      setProducts(prods.data || []);
      setDsettings(st.data || { codes_enabled: false, presets_enabled: false });
      await load('');
    } catch (e) { toast.error(t('discounts.loadError')); }
    finally { setLoading(false); }
  }, [load, t]);

  const toggleSetting = async (key, val) => {
    const prev = dsettings;
    setDsettings({ ...dsettings, [key]: val });
    try {
      await api.put('/discounts/settings', { [key]: val });
    } catch (e) {
      setDsettings(prev);
      toast.error(t('discounts.saveError', { defaultValue: 'Errore nel salvataggio' }));
    }
  };

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => {
    const id = setTimeout(() => { load(q).catch(() => {}); }, 300);
    return () => clearTimeout(id);
  }, [q, load]);

  const openNew = () => { setEditingId(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (d) => {
    setEditingId(d.id);
    setForm({
      code: d.code, type: d.type, value: String(d.value), scope: d.scope || 'all',
      category_ids: d.category_ids || [], product_ids: d.product_ids || [],
      max_uses: d.max_uses || 0, expires_at: d.expires_at ? String(d.expires_at).slice(0, 10) : '',
      active: d.active !== false,
    });
    setOpen(true);
  };

  const toggleId = (key, id) => setForm((f) => {
    const arr = f[key];
    return { ...f, [key]: arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id] };
  });

  const save = async () => {
    if (!form.code.trim()) { toast.error(t('discounts.errCode')); return; }
    const value = parseFloat(form.value);
    if (!value || value <= 0) { toast.error(t('discounts.errValue')); return; }
    if (form.type === 'percent' && value > 100) { toast.error(t('discounts.errPercent')); return; }
    if (form.scope === 'categories' && form.category_ids.length === 0) { toast.error(t('discounts.errCategory')); return; }
    if (form.scope === 'products' && form.product_ids.length === 0) { toast.error(t('discounts.errProduct')); return; }
    setSaving(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        type: form.type,
        value,
        scope: form.scope,
        category_ids: form.scope === 'categories' ? form.category_ids : [],
        product_ids: form.scope === 'products' ? form.product_ids : [],
        max_uses: parseInt(form.max_uses, 10) || 0,
        expires_at: form.expires_at || null,
        active: form.active,
      };
      if (editingId) await runSensitive((cfg) => api.put(`/discounts/${editingId}`, payload, cfg));
      else await runSensitive((cfg) => api.post('/discounts', payload, cfg));
      toast.success(editingId ? t('discounts.updated') : t('discounts.created'));
      setOpen(false);
      await load(q);
    } catch (e) {
      if (e?.message === 'cancelled') return;
      toast.error(e?.response?.data?.detail || t('discounts.saveError'));
    } finally { setSaving(false); }
  };

  const toggleActive = async (d) => {
    try { await runSensitive((cfg) => api.post(`/discounts/${d.id}/toggle`, {}, cfg)); await load(q); }
    catch (e) { if (e?.message !== 'cancelled') toast.error(e?.response?.data?.detail || t('common.error')); }
  };

  const remove = async (d) => {
    if (!window.confirm(t('discounts.deleteConfirm', { code: d.code }))) return;
    try { await runSensitive((cfg) => api.delete(`/discounts/${d.id}`, cfg)); toast.success(t('discounts.deleted')); await load(q); }
    catch (e) { if (e?.message !== 'cancelled') toast.error(e?.response?.data?.detail || t('common.error')); }
  };

  const scopeLabel = (d) => {
    if (d.scope === 'categories') return t('discounts.scopeCategories', { count: (d.category_ids || []).length });
    if (d.scope === 'products') return t('discounts.scopeProducts', { count: (d.product_ids || []).length });
    return t('discounts.scopeAll');
  };

  return (
    <div className="p-4 md:p-6 max-w-5xl" data-testid="discounts-page">
      <PageHeader title={t('discounts.title')} subtitle={t('discounts.subtitle')}>
        {canManage ? (
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-xl" onClick={() => setAuditOpen(true)} data-testid="discount-audit-button">
              <ScrollText className="h-4 w-4 mr-2" /> {t('discounts.register')}
            </Button>
            <Button className="rounded-xl" onClick={openNew} data-testid="discount-new-button">
              <Plus className="h-4 w-4 mr-2" /> {t('discounts.newCode')}
            </Button>
          </div>
        ) : (
          <Badge variant="outline" className="text-xs bg-muted text-muted-foreground" data-testid="discount-readonly-badge">
            <Lock className="h-3 w-3 mr-1" /> {t('discounts.readOnly')}
          </Badge>
        )}
      </PageHeader>

      {canManage && (
        <Card className="mb-4 p-4" data-testid="discount-switches">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="flex items-center justify-between gap-4 rounded-xl border border-border p-3">
              <div>
                <div className="font-medium">{t('discounts.codesSwitch', { defaultValue: 'Codici sconto' })}</div>
                <div className="text-sm text-muted-foreground">{t('discounts.codesSwitchDesc', { defaultValue: 'Mostra il campo per digitare un codice nel pagamento.' })}</div>
              </div>
              <Switch checked={!!dsettings.codes_enabled} onCheckedChange={(v) => toggleSetting('codes_enabled', v)} data-testid="discount-switch-codes" />
            </div>
            <div className="flex items-center justify-between gap-4 rounded-xl border border-border p-3">
              <div>
                <div className="font-medium">{t('discounts.presetsSwitch', { defaultValue: 'Sconti prefatti' })}</div>
                <div className="text-sm text-muted-foreground">{t('discounts.presetsSwitchDesc', { defaultValue: 'Mostra i pulsanti sconto rapidi nel pagamento.' })}</div>
              </div>
              <Switch checked={!!dsettings.presets_enabled} onCheckedChange={(v) => toggleSetting('presets_enabled', v)} data-testid="discount-switch-presets" />
            </div>
          </div>
        </Card>
      )}

      <div className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input className="pl-10" placeholder={t('discounts.searchPlaceholder')} value={q} onChange={(e) => setQ(e.target.value)} data-testid="discount-search-input" />
      </div>

      {loading ? (
        <div className="text-muted-foreground p-6">{t('common.loading')}</div>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground" data-testid="discount-empty">
          <Tag className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <div className="font-medium">{t('discounts.emptyTitle')}</div>
          <div className="text-sm">{t('discounts.emptyDesc')}</div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {items.map((d) => {
            const expired = d.expires_at && new Date(d.expires_at) < new Date();
            const exhausted = d.max_uses > 0 && d.used_count >= d.max_uses;
            return (
              <Card key={d.id} className="p-4" data-testid={`discount-item-${d.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold text-lg tracking-wider truncate">{d.code}</span>
                      {d.active !== false && !expired && !exhausted
                        ? <Badge variant="outline" className="text-[10px] bg-pos-free/10 text-pos-free border-pos-free/20">{t('discounts.stActive')}</Badge>
                        : <Badge variant="outline" className="text-[10px] bg-muted text-muted-foreground">{expired ? t('discounts.stExpired') : exhausted ? t('discounts.stExhausted') : t('discounts.stDisabled')}</Badge>}
                    </div>
                    <div className="text-2xl font-bold text-primary mt-1">
                      {d.type === 'percent' ? `${d.value}%` : `\u20ac ${Number(d.value).toFixed(2)}`}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                      <div>{scopeLabel(d)}</div>
                      {d.expires_at ? <div className="flex items-center gap-1"><CalendarClock className="h-3 w-3" /> {t('discounts.expiresOn', { date: fmtDate(d.expires_at) })}</div> : null}
                      <div>{t('discounts.uses')}: {d.used_count || 0}{d.max_uses > 0 ? ` / ${d.max_uses}` : ` ${t('discounts.unlimited')}`}</div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    {canManage ? (
                      <>
                        <Button size="icon" variant="ghost" onClick={() => openEdit(d)} data-testid={`discount-edit-${d.id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => toggleActive(d)} data-testid={`discount-toggle-${d.id}`} className={d.active !== false ? 'text-pos-free' : 'text-muted-foreground'}><Power className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => remove(d)} className="text-destructive hover:text-destructive" data-testid={`discount-delete-${d.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </>
                    ) : null}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Dialog: crea/modifica codice */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="discount-dialog">
          <DialogHeader><DialogTitle>{editingId ? t('discounts.editTitle') : t('discounts.newTitle')}</DialogTitle></DialogHeader>
          <ScrollArea className="max-h-[70vh] pr-3">
            <div className="space-y-4">
              <div>
                <Label>{t('discounts.code')}</Label>
                <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="ESTATE2025" className="font-mono uppercase" data-testid="discount-form-code" />
              </div>

              <div>
                <Label className="mb-1.5 block">{t('discounts.discountType')}</Label>
                <Tabs value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <TabsList className="grid grid-cols-2 w-full">
                    <TabsTrigger value="percent" data-testid="discount-type-percent"><Percent className="h-4 w-4 mr-1" /> {t('discounts.percent')}</TabsTrigger>
                    <TabsTrigger value="fixed" data-testid="discount-type-fixed"><Euro className="h-4 w-4 mr-1" /> {t('discounts.fixed')}</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>

              <div>
                <Label>{form.type === 'percent' ? t('discounts.percentValue') : t('discounts.amountValue')}</Label>
                <AmountInput value={form.value} onValueChange={(v) => setForm({ ...form, value: v })} label={form.type === 'percent' ? t('discounts.percentLabel') : t('discounts.amountLabel')} suffix={form.type === 'percent' ? '%' : '\u20ac'} placeholder={form.type === 'percent' ? '10' : '5.00'} data-testid="discount-form-value" />
              </div>

              <div>
                <Label className="mb-1.5 block">{t('discounts.applicableTo')}</Label>
                <Tabs value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v })}>
                  <TabsList className="grid grid-cols-3 w-full">
                    <TabsTrigger value="all" data-testid="discount-scope-all">{t('discounts.scopeAllTab')}</TabsTrigger>
                    <TabsTrigger value="categories" data-testid="discount-scope-categories">{t('discounts.categories')}</TabsTrigger>
                    <TabsTrigger value="products" data-testid="discount-scope-products">{t('discounts.products')}</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>

              {form.scope === 'categories' && (
                <div className="rounded-xl border border-border p-2 max-h-48 overflow-auto space-y-1" data-testid="discount-categories-list">
                  {categories.length === 0 ? <div className="text-sm text-muted-foreground p-2">{t('discounts.noCategories')}</div> : categories.map((c) => (
                    <label key={c.id} className="flex items-center gap-2 px-2 py-2 rounded-lg cursor-pointer hover:bg-muted text-sm">
                      <input type="checkbox" checked={form.category_ids.includes(c.id)} onChange={() => toggleId('category_ids', c.id)} className="h-4 w-4" />
                      {c.name}
                    </label>
                  ))}
                </div>
              )}

              {form.scope === 'products' && (
                <div className="rounded-xl border border-border p-2 max-h-48 overflow-auto space-y-1" data-testid="discount-products-list">
                  {products.length === 0 ? <div className="text-sm text-muted-foreground p-2">{t('discounts.noProducts')}</div> : products.map((p) => (
                    <label key={p.id} className="flex items-center gap-2 px-2 py-2 rounded-lg cursor-pointer hover:bg-muted text-sm">
                      <input type="checkbox" checked={form.product_ids.includes(p.id)} onChange={() => toggleId('product_ids', p.id)} className="h-4 w-4" />
                      {p.name}
                    </label>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t('discounts.expiryOptional')}</Label>
                  <Input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} data-testid="discount-form-expires" />
                </div>
                <div>
                  <Label>{t('discounts.usageLimit')}</Label>
                  <Input type="number" inputMode="numeric" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} data-testid="discount-form-maxuses" />
                </div>
              </div>

              <div className="flex items-center justify-between rounded-xl border border-border p-3">
                <div>
                  <div className="font-medium">{t('discounts.active')}</div>
                  <div className="text-xs text-muted-foreground">{t('discounts.activeDesc')}</div>
                </div>
                <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="discount-form-active" />
              </div>
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
            <Button className="rounded-xl" onClick={save} disabled={saving} data-testid="discount-form-save">{editingId ? t('common.save') : t('discounts.createCode')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AuditLogDialog open={auditOpen} onOpenChange={setAuditOpen} entityType="discount" title={t('discounts.auditTitle')} />
    </div>
  );
}
